import React, { Suspense, useEffect, useState, useRef } from "react";
import { BrowserRouter as Router, Switch, Route, Redirect } from "react-router-dom";
import firebase from "firebase/app";
import "firebase/messaging";
import { initFirebase } from "./firebaseInit";

import SignUpContainer from "./containers/SignUpContainer";
import DoctorSignUpContainer from "./containers/DoctorSignUpContainer";
import LoginContainer from "./containers/LoginContainer";
import Dashboard from "./containers/Dashboard";
import TestComponent from "./components/boxes/TestComponent";
import "./App.css";
import "./index.css";
import ResetPasswordContainer from "./containers/ResetPasswordContainer";
import PlanExpiredPage from "./components/PlanExpiredPage";
import PaymentSuccessfulPage from "./components/PaymentSuccessfulPage";
import LogoutContainer from "./containers/LogoutContainer";
import { withUser } from "./components/utils/AuthHOC";
import LanguageSwitcher from "./components/languageSwitcher/LanguageSwitcher";
import { PulseLoader } from "react-spinners";
import { useFeatureFlags } from "./components/utils/FeatureFlagWrapper";
import ReferContainer from "./containers/ReferContainer";
import Localization from "./utils/localization";
import LoadingScreen from "./components/LoadingScreen";
import userService from "./services/userService";
import { getSubscription } from "./services/careBalanceService";
import { getCookie } from "./services/utilityService";
import { shouldRouteExpiredPlanUsers } from "./components/care-balance/utils/should-route-to-expiry-page";

// 🔥 Initialize Firebase BEFORE React renders anything
if (!process.env.DISABLE_FIREBASE) {
  initFirebase();
}

const PUBLIC_PATHS = [
  "/login",
  "/register",
  "/logout",
  "/reset-password",
  "/plan-expired",
  "/payment-successful",
  "/test",
  "/doctor-signup",
];

// Checks profile/plan before rendering protected routes
const AppOldRoutes = () => {
  const isPublicPath = PUBLIC_PATHS.includes(window.location.pathname);

  // Block the Switch immediately for cookie-authenticated sessions on protected paths
  const [statusLoading, setStatusLoading] = useState(!isPublicPath && !!getCookie());
  const [isInactiveUser, setIsInactiveUser] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    const cookie = getCookie();
    if (!cookie || hasChecked.current || isPublicPath) return;

    hasChecked.current = true;
    setStatusLoading(true);

    const checkUserStatus = async () => {
      try {
        const profile = await userService.getProfile();
        if (profile?.status === "error" && profile?.message === "Inactive profile") {
          setIsInactiveUser(true);
          return;
        }
        const plan = await getSubscription();
        if (shouldRouteExpiredPlanUsers({ planInfo: plan })) {
          setIsInactiveUser(true);
        }
      } catch (err) {
        if (
          err?.message === "Inactive profile" ||
          err?.errorMessage === "Inactive profile"
        ) {
          setIsInactiveUser(true);
        }
      } finally {
        setStatusLoading(false);
      }
    };

    checkUserStatus();
  }, []);

  if (statusLoading) return <LoadingScreen />;

  return (
    <Switch>
      <Route component={LoginContainer} path="/login" exact />
      <Route component={SignUpContainer} path="/register" exact />
      <Route component={DoctorSignUpContainer} path="/doctor-signup" exact />
      <Route component={ResetPasswordContainer} path="/reset-password" exact />
      <Route component={TestComponent} path="/test" exact />
      <Route component={PlanExpiredPage} path="/plan-expired" exact />
      <Route component={PaymentSuccessfulPage} path="/payment-successful" exact />
      <Route component={LogoutContainer} path="/logout" exact />
      {isInactiveUser ? (
        <Redirect to="/plan-expired" />
      ) : (
        <>
          <Route component={withUser(ReferContainer)} path="/refer" />
          <Route component={withUser(Dashboard)} path="/" />
        </>
      )}
    </Switch>
  );
};

const AppOld = () => {
  const { flags } = useFeatureFlags();

  // 🔹 Service worker notification toggle
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.active?.postMessage({
          type: "TOGGLE_NOTIFICATIONS",
          enabled: JSON.parse(
            localStorage.getItem("notificationsEnabled") || "true",
          ),
        });
      });
    }
  }, []);

  // 🔹 Safe FCM foreground message listener (iOS protected)
  useEffect(() => {
    let unsubscribe;

    const setupMessaging = async () => {
      if (!firebase.apps.length) return;

      const supported = await firebase.messaging.isSupported();
      if (!supported) {
        console.log("FCM not supported on this device/browser");
        return;
      }

      const messaging = firebase.messaging();

      unsubscribe = messaging.onMessage((payload) => {
        try {
          const parsed = JSON.parse(payload.data.data);

          const isCategoryPicked = parsed.category === 6;
          const isTypePicked = parsed.type === 2;
          const messageId = parsed.message.id;

          const isConsultationPathMatch =
            window.location.pathname === `/consultations/${messageId}/messages`;

          if (isCategoryPicked && isTypePicked && isConsultationPathMatch) {
            window.location.reload();
          } else {
            console.info("FCM received but not on target page");
          }
        } catch (err) {
          console.error("Failed to parse FCM payload", err);
        }
      });
    };

    setupMessaging();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return (
    <Suspense
      fallback={
        <div className="loader-fb">
          <PulseLoader color="#094063" size={7} />
        </div>
      }
    >
      <Router>
        <div className="content dash">
          {Localization.isLanguageOptionOptionEnabled() && <LanguageSwitcher />}
          <AppOldRoutes />
        </div>
      </Router>
    </Suspense>
  );
};

export default AppOld;
